import { useEffect, useRef } from 'react';
import io, { type Socket } from 'socket.io-client';
import { useGame } from '../state/GameContext';
import { API_BASE } from '../api/config';
import { sounds, haptics } from '../utils/sounds';
import { particles } from '../utils/particles';
import type { GameState, PhotoData, PhotoResults } from '../types/game';

export function useWebSocket() {
  const { state, dispatch } = useGame();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const { roomCode, playerId, playerName, authToken, isHost } = state;
    if (!roomCode || !playerId || !playerName || !authToken) return;

    const newSocket = io(API_BASE, { auth: { token: authToken } });
    socketRef.current = newSocket;
    dispatch({ type: 'SET_FIELD', field: 'socket', value: newSocket });

    newSocket.on('connect_error', () => {
      dispatch({ type: 'SHOW_ERROR', message: 'Connection failed. Please refresh and try again.' });
    });

    newSocket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') newSocket.connect();
    });

    newSocket.on('error', () => {
      dispatch({ type: 'SHOW_ERROR', message: 'Connection error. Please try again.' });
    });

    newSocket.emit('joinRoom', { roomCode, playerId, playerName, isHost });

    newSocket.on('gameState', (gs: GameState) => {
      dispatch({ type: 'UPDATE_GAME_STATE', state: gs });
    });

    newSocket.on('playerJoined', (data: { playerName: string }) => {
      dispatch({ type: 'SHOW_SUCCESS', message: `${data.playerName} joined the game!` });
      setTimeout(() => dispatch({ type: 'CLEAR_MESSAGES' }), 3000);
      // Request fresh game state so player list updates
      newSocket.emit('requestGameState');
    });

    newSocket.on('playerLeft', (data: { playerName: string }) => {
      dispatch({ type: 'SHOW_SUCCESS', message: `${data.playerName} left the game` });
      setTimeout(() => dispatch({ type: 'CLEAR_MESSAGES' }), 3000);
      // Request fresh game state so player list updates
      newSocket.emit('requestGameState');
    });

    newSocket.on('photoUploaded', (data: { playerName: string; totalPhotos: number; canStartGame: boolean }) => {
      // Don't manually update gameState here — the server already emits a full
      // 'gameState' event after each upload with updated player photo counts.
      // Using stale closure state here was overwriting fresh player data.
      dispatch({ type: 'SHOW_SUCCESS', message: `${data.playerName} uploaded a photo!` });
      setTimeout(() => dispatch({ type: 'CLEAR_MESSAGES' }), 3000);
    });

    newSocket.on('gameStarted', () => {
      dispatch({ type: 'SET_FIELD', field: 'currentView', value: 'game' });
      dispatch({ type: 'SHOW_SUCCESS', message: 'Game started! Get ready...' });
      sounds.notification();
      haptics.medium();
      setTimeout(() => dispatch({ type: 'CLEAR_MESSAGES' }), 3000);
    });

    newSocket.on('newPhoto', (data: PhotoData) => {
      dispatch({ type: 'NEW_PHOTO', photo: data });
    });

    newSocket.on('photoResults', (data: PhotoResults) => {
      dispatch({ type: 'PHOTO_RESULTS', results: data });

      const myGuess = data.guesses.find((g) => g.guesser === playerName);
      if (myGuess?.correct) {
        sounds.success();
        haptics.success();
        dispatch({ type: 'SET_FIELD', field: 'showConfetti', value: true });
        setTimeout(() => dispatch({ type: 'SET_FIELD', field: 'showConfetti', value: false }), 3000);

        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        particles.emojiBurst(cx, cy, '✨');

        const myScore = data.leaderboard.find((p) => p.id === playerId);
        if (myScore?.score) particles.celebrateScore(cx, cy, 100);
        if (myScore?.streak && myScore.streak >= 3) {
          setTimeout(() => particles.streakCelebration(cx, cy, myScore.streak!), 500);
        }
      } else if (myGuess) {
        sounds.error();
        haptics.error();
      }

      // Don't clear photoResults on a timer — the next 'newPhoto' or 'gameFinished'
      // event will handle the transition. The old 5s timeout raced with the server's
      // 5s delay, causing standings to vanish before the next photo arrived.
    });

    newSocket.on('gameFinished', (data: { leaderboard: PhotoResults['leaderboard'] }) => {
      dispatch({ type: 'GAME_FINISHED', leaderboard: data.leaderboard });
      sounds.gameFinish();
      haptics.success();
    });

    newSocket.on('gameReset', (gs: GameState) => {
      dispatch({ type: 'GAME_RESET', state: gs });
      dispatch({ type: 'SHOW_SUCCESS', message: 'Game reset! Upload new photos to play again.' });
      setTimeout(() => dispatch({ type: 'CLEAR_MESSAGES' }), 3000);
    });

    return () => {
      newSocket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.roomCode, state.playerId, state.playerName, state.authToken]);

  return socketRef;
}
